import React, { useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Merge, Loader2, Search, ArrowRight, CheckCircle2 } from "lucide-react";

const ROUNDS = ["hr", "technical", "behavioral"];
const ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };

// Client-side hint only — the backend's _detect_seniority is authoritative.
const detectBand = (name) => {
  const t = (name || "").toLowerCase();
  if (/\b(junior|jr|entry|associate|graduate|intern)\b/.test(t)) return "Easy";
  if (/\b(senior|sr|lead|principal|staff|head|director|vp|manager)\b/.test(t)) return "Hard";
  return "Medium";
};

const stripSeniority = (name) =>
  (name || "")
    .replace(/\b(junior|jr\.?|senior|sr\.?|lead|principal|staff|entry[- ]?level|associate|graduate|intern|mid[- ]?level|intermediate|trainee|i{1,3}|iv|v)\b/gi, "")
    .replace(/\(.*?\)/g, "")
    .replace(/\s+/g, " ")
    .trim();

const BAND_STYLES = {
  Easy: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  Medium: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
  Hard: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30",
};
const BandChip = ({ band }) => (
  <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${BAND_STYLES[band] || BAND_STYLES.Medium}`}>
    {band}
  </span>
);

export default function MergeRolesDialog({ open, onClose, roles, industries, families, onMerged }) {
  const [selected, setSelected] = useState(new Set());
  const [targetName, setTargetName] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);

  const industryMap = useMemo(() => Object.fromEntries(industries.map((i) => [i.industry_id, i.name])), [industries]);
  const familyMap = useMemo(() => Object.fromEntries(families.map((f) => [f.family_id, f.name])), [families]);

  // Only active, not-already-merged roles can be merge sources.
  const eligible = useMemo(
    () => roles.filter((r) => r.is_active && !r.merged_into),
    [roles]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? eligible.filter((r) => r.name.toLowerCase().includes(q)) : eligible;
    // group by "Industry › Family" for easy sibling spotting
    const groups = {};
    for (const r of list) {
      const key = `${industryMap[r.industry_id] || "Uncategorized"} › ${familyMap[r.family_id] || "No family"}`;
      (groups[key] = groups[key] || []).push(r);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [eligible, search, industryMap, familyMap]);

  const reset = () => {
    setSelected(new Set());
    setTargetName("");
    setSearch("");
    setPreview(null);
    setLoadingPreview(false);
    setMerging(false);
  };

  const close = () => { reset(); onClose(); };

  const toggle = (roleId, roleName) => {
    setPreview(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else {
        next.add(roleId);
        // prefill a sensible unified name from the first picked sibling
        if (!targetName.trim()) setTargetName(stripSeniority(roleName));
      }
      return next;
    });
  };

  const selectedRoles = eligible.filter((r) => selected.has(r.role_id));

  const runPreview = async () => {
    if (selected.size < 2) { toast.error("Select at least two roles"); return; }
    if (!targetName.trim()) { toast.error("Enter a unified role name"); return; }
    setLoadingPreview(true);
    try {
      const { data } = await api.post("/admin/roles/merge/preview", {
        source_role_ids: Array.from(selected),
        target_name: targetName.trim(),
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview merge");
    } finally { setLoadingPreview(false); }
  };

  const runMerge = async () => {
    setMerging(true);
    try {
      const { data } = await api.post("/admin/roles/merge", {
        source_role_ids: Array.from(selected),
        target_name: targetName.trim(),
      });
      toast.success(`Merged ${selected.size} roles into "${data.name}"`);
      const newId = data.role_id;
      close();
      onMerged?.(newId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not merge roles");
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="rounded-xl max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold flex items-center gap-2">
            <Merge className="w-5 h-5 text-[#1D4ED8]" /> Merge roles into one
          </DialogTitle>
          <DialogDescription>
            Combine per-seniority siblings (e.g. Junior / Intermediate / Lead QA) into a single role.
            Each source's questions are tagged by its seniority band, deduped, and merged. Candidates then
            pick an experience level at interview time. The source roles are <strong>hidden but kept</strong> (reversible).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Unified name */}
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Unified role name</Label>
            <Input
              value={targetName}
              onChange={(e) => { setTargetName(e.target.value); setPreview(null); }}
              placeholder="e.g. QA Engineer"
              className="rounded-xl py-6"
            />
          </div>

          {/* Source picker */}
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">
              Select roles to merge <span className="text-muted-foreground normal-case font-normal">({selected.size} selected)</span>
            </Label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter roles…" className="rounded-xl pl-9" />
            </div>
            <div className="border border-border rounded-xl max-h-64 overflow-y-auto divide-y divide-border/60">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground font-mono">No eligible roles.</div>
              ) : filtered.map(([group, groupRoles]) => (
                <div key={group}>
                  <div className="px-3 py-1.5 bg-secondary/40 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground sticky top-0">
                    {group}
                  </div>
                  {groupRoles.map((r) => {
                    const total = ROUNDS.reduce((s, rnd) => s + (r[`${rnd}_question_count`] || 0), 0);
                    const checked = selected.has(r.role_id);
                    return (
                      <button
                        key={r.role_id}
                        type="button"
                        onClick={() => toggle(r.role_id, r.name)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${checked ? "bg-[#1D4ED8]/5" : "hover:bg-secondary/30"}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-[#1D4ED8] border-[#1D4ED8]" : "border-muted-foreground/40"}`}>
                          {checked && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="font-semibold text-sm truncate block">{r.name}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">{total} Qs</span>
                        </span>
                        <BandChip band={detectBand(r.name)} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Preview panel */}
          {preview && (
            <div className="border border-[#1D4ED8]/30 rounded-xl bg-[#1D4ED8]/[0.03] p-4 space-y-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8] font-bold">Merge preview</div>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                {preview.sources.map((s, i) => (
                  <React.Fragment key={s.role_id}>
                    {i > 0 && <span className="text-muted-foreground">+</span>}
                    <span className="inline-flex items-center gap-1.5">
                      {s.name} <BandChip band={s.band} />
                    </span>
                  </React.Fragment>
                ))}
                <ArrowRight className="w-4 h-4 text-[#1D4ED8]" />
                <span className="font-bold">{preview.target_name}</span>
              </div>

              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-1">Round</th>
                    <th className="text-center py-1 text-[#10B981]">Easy</th>
                    <th className="text-center py-1 text-[#B45309]">Medium</th>
                    <th className="text-center py-1 text-[#E11D48]">Hard</th>
                    <th className="text-center py-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ROUNDS.map((rnd) => {
                    const row = preview.stats.rounds[rnd] || { Easy: 0, Medium: 0, Hard: 0, total: 0 };
                    return (
                      <tr key={rnd} className="border-t border-border/50">
                        <td className="py-1.5 font-medium">{ROUND_LABELS[rnd]}</td>
                        <td className="text-center">{row.Easy}</td>
                        <td className="text-center">{row.Medium}</td>
                        <td className="text-center">{row.Hard}</td>
                        <td className="text-center font-semibold">{row.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="font-mono text-[11px] text-muted-foreground">
                {preview.stats.total} questions total · {preview.stats.duplicates_removed} duplicate{preview.stats.duplicates_removed !== 1 ? "s" : ""} removed
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={close}>Cancel</Button>
          {!preview ? (
            <Button onClick={runPreview} disabled={loadingPreview || selected.size < 2 || !targetName.trim()}
              className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              {loadingPreview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Previewing…</> : <>Preview merge</>}
            </Button>
          ) : (
            <Button onClick={runMerge} disabled={merging}
              className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]">
              {merging ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Merging…</> : <><Merge className="w-4 h-4 mr-2" /> Confirm merge</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
